'use client';

import { getSafeAuthClientMessage } from '@/features/auth/utils/getSafeAuthClientMessage';
import { clientFetch } from '@/lib/api/clientFetch';
import { useCallback, useEffect, useRef, useState } from 'react';

const GOOGLE_GSI_SRC = 'https://accounts.google.com/gsi/client';

function ensureGoogleScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(
      new Error('Google Sign-In is only available in the browser.'),
    );
  }

  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }

  const existingScript = document.querySelector(
    `script[src="${GOOGLE_GSI_SRC}"]`,
  );

  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Failed to load Google Sign-In.')),
        { once: true },
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GOOGLE_GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Sign-In.'));
    document.head.appendChild(script);
  });
}

export function useGoogleAuth({
  redirectTarget = '/account',
  onSuccess,
  onError,
} = {}) {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const clientIdRef = useRef('');
  const initializedRef = useRef(false);
  const redirectTargetRef = useRef(redirectTarget);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    redirectTargetRef.current = redirectTarget;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onError, onSuccess, redirectTarget]);

  const handleError = useCallback((message) => {
    const nextMessage = message || 'Google sign-in is unavailable right now.';
    onErrorRef.current?.(nextMessage);
  }, []);

  const handleCredentialResponse = useCallback(
    async (response) => {
      const idToken = response?.credential;

      if (!idToken) {
        handleError('No Google credential was returned.');
        return;
      }

      try {
        const data = await clientFetch('/api/users/auth/google', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({ idToken }),
        });

        await onSuccessRef.current?.(data, redirectTargetRef.current);
      } catch (error) {
        handleError(
          getSafeAuthClientMessage(
            error,
            'Google sign-in failed. Please try again.',
          ),
        );
      }
    },
    [handleError],
  );

  useEffect(() => {
    let isCancelled = false;

    const initialize = async () => {
      setIsLoading(true);

      try {
        await ensureGoogleScript();

        const config = await clientFetch('/api/config/google');
        const clientId = String(config?.clientId || '').trim();

        if (!clientId) {
          throw new Error('Google sign-in is not configured.');
        }

        clientIdRef.current = clientId;

        if (!initializedRef.current) {
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleCredentialResponse,
            use_fedcm_for_prompt: false,
            ux_mode: 'popup',
            auto_select: false,
            cancel_on_tap_outside: true,
          });
          initializedRef.current = true;
        }

        if (!isCancelled) {
          setIsReady(true);
        }
      } catch (error) {
        if (!isCancelled) {
          setIsReady(false);
          handleError(error?.message);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    initialize();

    return () => {
      isCancelled = true;
    };
  }, [handleCredentialResponse, handleError]);

  const signIn = useCallback(() => {
    if (!window.google?.accounts?.id || !clientIdRef.current) {
      handleError('Google sign-in is not ready yet.');
      return;
    }

    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed?.() || notification.isSkippedMoment?.()) {
        window.google.accounts.id.prompt();
      }
    });
  }, [handleError]);

  return {
    isReady,
    isLoading,
    signIn,
  };
}
